import { useCallback } from 'react';
import { CHAT_BLK } from '../constants';
import { ChatBlkTelemetryService } from '../services';
import { telemetryFunctionMap } from '../types';
import { isLocalhost } from '../utils';

/**
 * Custom hook to handle BXF events with optional telemetry tracking.
 *
 * @param callback - The callback function to be executed when the event is triggered. It can return a value or a Promise.
 * @param telemetryArgs - Optional telemetry arguments for tracking the event. It can be an object containing `chatBlkFunction`, `options`, and `queryResponseId`, or a single `chatBlkFunction`.
 *
 * @returns A memoized callback function that handles the event, executes the provided callback, and optionally tracks telemetry data.
 *
 * @example
 * const handleEvent = useHandleBxfEvent(async (args) => {
 *   // Your event handling logic here
 * }, {
 *   chatBlkFunction: someFunction,
 *   options: { key: 'value' },
 *   queryResponseId: 'some-id'
 * });
 *
 * // Usage in a component
 * useEffect(() => {
 *   window.addEventListener('someEvent', handleEvent);
 *   return () => {
 *     window.removeEventListener('someEvent', handleEvent);
 *   };
 * }, [handleEvent]);
 */
const useHandleBxfEvent = (
    callback: (...args: unknown[]) => unknown | Promise<unknown>,
    telemetryArgs?:
        | {
              chatBlkFunction: (typeof telemetryFunctionMap)[keyof typeof telemetryFunctionMap];
              options?: object;
              queryResponseId?: string;
          }
        | (typeof telemetryFunctionMap)[keyof typeof telemetryFunctionMap]
) => {
    return useCallback(
        async (event: CustomEvent) => {
            const eventName = `[${CHAT_BLK} ${event.type?.toUpperCase() || 'unknown'}]`;
            const args = event.detail;
            try {
                isLocalhost() &&
                    console.log(
                        `${eventName} event triggered with args:`,
                        args
                    );

                await callback();

                if (!telemetryArgs) return;

                if (typeof telemetryArgs === 'object') {
                    const { chatBlkFunction, options, queryResponseId } =
                        telemetryArgs;
                    ChatBlkTelemetryService.track(
                        chatBlkFunction,
                        options,
                        queryResponseId
                    );
                } else {
                    ChatBlkTelemetryService.track(telemetryArgs);
                }
            } catch (error) {
                console.error(
                    `[${CHAT_BLK} ${eventName}] Error in event callback:`,
                    error
                );
            }
        },
        [telemetryArgs, callback]
    );
};

export default useHandleBxfEvent;
